import { useState, useMemo } from 'react'
import { BookOpen, RefreshCw } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { PageShell } from '@/components/common/PageShell'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState } from '@/components/common/EmptyState'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { GlossaryStatusBadge, GlossaryTermDialog } from '@/components/knowledge'
import { useGlossarySearch, useRebuildGlossary } from '@/api/knowledge'
import { usePermissions } from '@/hooks/usePermissions'
import { useDebounce } from '@/hooks/useDebounce'
import type { GlossaryTerm, GlossaryTermStatus } from '@/types/knowledge'

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All Statuses' },
  { value: 'Proposed', label: 'Proposed' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'Deprecated', label: 'Deprecated' },
]

export function GlossaryPage() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedTerm, setSelectedTerm] = useState<GlossaryTerm | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  const { hasPermission } = usePermissions()
  const canEdit = hasPermission('config:edit')

  const debouncedSearch = useDebounce(search, 300)

  const { data: terms, isLoading } = useGlossarySearch({
    query: debouncedSearch,
    maxResults: 200,
  })

  const rebuildGlossary = useRebuildGlossary()

  const filteredTerms = useMemo(() => {
    if (!terms) return []
    if (statusFilter === 'all') return terms
    return terms.filter((t) => t.status === statusFilter)
  }, [terms, statusFilter])

  const handleRowClick = (term: GlossaryTerm) => {
    setSelectedTerm(term)
    setDialogOpen(true)
  }

  return (
    <PageShell>
      <div className="p-6 space-y-6">
        <PageHeader
          title="Glossary"
          description="Browse and curate domain terminology extracted from knowledge sources"
          actions={
            canEdit ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" disabled={rebuildGlossary.isPending}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${rebuildGlossary.isPending ? 'animate-spin' : ''}`} />
                    Rebuild Glossary
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Rebuild Glossary</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will re-extract terms from all indexed documents and rebuild the glossary.
                      Approved and rejected terms will retain their status.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => rebuildGlossary.mutate({ fullRebuild: true })}
                    >
                      Rebuild
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : undefined
          }
        />

        <div className="flex items-center gap-4">
          <div className="flex-1 max-w-md">
            <Input
              placeholder="Search terms..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isLoading && (
          <LoadingSpinner className="py-12" />
        )}

        {!isLoading && filteredTerms.length > 0 && (
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Term</TableHead>
                  <TableHead>Aliases</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Language</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTerms.map((term) => (
                  <TableRow
                    key={term.termId}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleRowClick(term)}
                  >
                    <TableCell className="font-medium">{term.canonicalName}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {term.aliases.length > 0 ? term.aliases.join(', ') : '—'}
                    </TableCell>
                    <TableCell>
                      <GlossaryStatusBadge status={term.status as GlossaryTermStatus} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {term.language ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(term.updatedAt), { addSuffix: true })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}

        {!isLoading && filteredTerms.length === 0 && (
          <EmptyState
            icon={BookOpen}
            title="No terms found"
            description={
              search || statusFilter !== 'all'
                ? 'Try adjusting your search or status filter.'
                : 'No glossary terms have been extracted yet.'
            }
          />
        )}

        <GlossaryTermDialog
          term={selectedTerm}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          canEdit={canEdit}
        />
      </div>
    </PageShell>
  )
}
